import { useMemo } from "react";
import moment from "jalali-moment";
import { CroppingSuitabilityModel } from "@agroyaar/sdk";
import ReportPage from "@/shared/components/report-template/presentation/pages/ReportPage";
import Introduction from "@/shared/components/report-template/presentation/components/introduction/Introduction";
import CroppingSuitabilityDefinition from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/CroppingSuitabilityDefinition";
import FinalLandSuitabilityIndex from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/FinalLandSuitabilityIndex";
import HelpLandSuitabilityClass from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/HelpLandSuitabilityClass";
import AnnuallyPerformancePotential from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/AnnuallyPerformancePotential";
import DailyTemperatureChange from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/DailyTemperatureChange";
import TemperatureRisks from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/TemperatureRisks";
import HumidityRisks from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/HumidityRisks";
import AveragesGdd from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/AveragesGdd";
import IrrigationRequirement from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/IrrigationRequirement";
import LandSuitabilityIndexForParameters from "@/features/reports/presentation/components/cropping-suitability-multi-product/components/LandSuitabilityIndexForParameters";

interface CroppingSuitabilityReportPageProps {
  isLoading: boolean;
  croppingSuitability: CroppingSuitabilityModel | undefined;
}

const CroppingSuitabilityReportPage = ({
  isLoading,
  croppingSuitability,
}: CroppingSuitabilityReportPageProps) => {
  const temperatureRisksChartPerPage = 2;
  const temperatureRisksPage = useMemo(
    () =>
      croppingSuitability?.reports &&
      Math.ceil(
        croppingSuitability?.reports?.length / temperatureRisksChartPerPage
      ),
    [croppingSuitability?.reports]
  );

  const chartYears = useMemo(
    () =>
      croppingSuitability?.reports[0]?.annuallyNetIrrigationRequirement?.map(
        ({ year }) => moment(`${year}-04-01`).format("jYYYY")
      ),
    [croppingSuitability?.reports]
  );

  return (
    <div>
      <ReportPage isLoading={isLoading}>
        {croppingSuitability && (
          <>
            <Introduction
              serviceType="CROPPING_SUITABILITY"
              farmer={croppingSuitability.farmer}
              farm={croppingSuitability.farm}
              irrigationSource={croppingSuitability.irrigationSource}
              introDes={
                <p className="text-[#222] text-[24px] !font-[B-Nazanin] mx-auto text-center">
                  ارزیابی{" "}
                  <span className="text-president text-[24px] !font-[B-Nazanin]">
                    {croppingSuitability.reports
                      .map((cropReport) => cropReport.crop.persianName)
                      .join("، ")}
                  </span>
                </p>
              }
            />

            <CroppingSuitabilityDefinition
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <FinalLandSuitabilityIndex
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <HelpLandSuitabilityClass
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <LandSuitabilityIndexForParameters
              croppingSuitabilityReports={croppingSuitability.reports}
            />

            <AnnuallyPerformancePotential
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
            />

            <DailyTemperatureChange
              dailyMaximumTemperatureChange={
                croppingSuitability.dailyMaximumTemperatureChange
              }
              dailyMeanTemperatureChange={
                croppingSuitability.dailyMeanTemperatureChange
              }
              dailyMinimumTemperatureChange={
                croppingSuitability.dailyMinimumTemperatureChange
              }
            />

            <TemperatureRisks
              croppingSuitabilityReports={croppingSuitability.reports}
              temperatureRisksPage={temperatureRisksPage!}
              temperatureRisksChartPerPage={temperatureRisksChartPerPage}
            />

            <HumidityRisks
              croppingSuitabilityReports={croppingSuitability.reports}
              pageNumber={9 + temperatureRisksPage!}
            />

            <AveragesGdd
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
              pageNumber={10 + temperatureRisksPage!}
            />

            <IrrigationRequirement
              croppingSuitabilityReports={croppingSuitability.reports}
              chartYears={chartYears!}
              pageNumber={11 + temperatureRisksPage!}
            />
          </>
        )}
      </ReportPage>
    </div>
  );
};

export default CroppingSuitabilityReportPage;
